import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { userRoles, planNames } from 'src/common/config/TextMessage';

/**
 * Interface for user type information
 */
interface UserType {
  roleType: string | null;
  isMember: boolean;
  isAdmin: boolean;
  userDetails: any;
}

/**
 * Interface for plan information object
 */
interface PlanInfo {
  activePlan: string;
  planDetails: RootState['user']['planDetails'];
  subscription: any;
  premiumUser: boolean;
  isDIYUser: boolean;
  isAgency: boolean;
  isDesigner: boolean;
  isLocalSeoPlan: boolean;
  isEnterprisePlan: boolean;
  isNewPlan: boolean | string;
  primePlan: string[];
  stripeData: any;
  isDowngradedWithSites: boolean | null;
  // Plan type flags
  isFreePlan: boolean;
  isDiyPlan: boolean;
  isDesignerPlan: boolean;
  isAgencyPlan: boolean;
  isLocalSeoPlanActive: boolean;
  isEnterprisePlanActive: boolean;
  isDiyOrFreePlan: boolean;
  isPrimePlan: boolean;
  isProPlan: boolean;
  // Subscription flags
  hasSubscription: boolean;
  isSubscriptionPaused: boolean;
}

/**
 * Return type for useUserTypeAndPlan hook
 */
export interface UseUserTypeAndPlanReturn {
  // User type information
  userType: UserType;
  roleType: string | null;
  isMember: boolean;
  isAdmin: boolean;
  userDetails: any;
  isLoadingUserDetails: boolean;

  // Plan details
  planDetails: PlanInfo;
  activePlan: string;
  planDetailsMap: RootState['user']['planDetails'];
  subscription: any;
  premiumUser: boolean;
  stripeData: any;

  // Plan type flags
  isFreePlan: boolean;
  isDiyPlan: boolean;
  isDesignerPlan: boolean;
  isAgencyPlan: boolean;
  isLocalSeoPlanActive: boolean;
  isEnterprisePlanActive: boolean;
  isDiyOrFreePlan: boolean;
  isPrimePlan: boolean;
  isProPlan: boolean;

  // User plan flags (from Redux)
  isDIYUser: boolean;
  isAgency: boolean;
  isDesigner: boolean;
  isLocalSeoPlan: boolean;
  isEnterprisePlan: boolean;
  isNewPlan: boolean | string;
  isDowngradedWithSites: boolean | null;

  // Subscription flags
  hasSubscription: boolean;
  isSubscriptionPaused: boolean;

  // Prime plan array
  primePlan: string[];
}

/**
 * Custom hook to check user type and plan details
 * 
 * This hook provides a centralized way to access user type information and plan details
 * from the Redux store. It returns computed values for easy use throughout the application.
 * 
 * @returns {Object} An object containing:
 * - User type information (roleType, isMember, isAdmin, userDetails)
 * - Plan details (activePlan, planDetails, subscription, etc.)
 * - Plan type flags (isFreePlan, isAgencyPlan, isEnterprisePlan, etc.)
 * - Subscription status flags (hasSubscription, isSubscriptionPaused)
 * 
 * @example
 * // Basic usage
 * const { isMember, isFreePlan, activePlan } = useUserTypeAndPlan();
 * 
 * @example
 * // Check user type
 * const { isMember, isAdmin, roleType } = useUserTypeAndPlan();
 * if (isMember) {
 *   // Handle member user
 * }
 * 
 * @example
 * // Check plan details
 * const { isAgencyPlan, isEnterprisePlan, premiumUser } = useUserTypeAndPlan();
 * if (isAgencyPlan || isEnterprisePlan) {
 *   // Show premium features
 * }
 * 
 * @example
 * // Access full plan information
 * const { planDetails, subscription, hasSubscription } = useUserTypeAndPlan();
 * const planInfo = planDetails; // Contains all plan-related information
 */
export const useUserTypeAndPlan = (): UseUserTypeAndPlanReturn => {
  const isLoadingUserDetails = useSelector(
    (state: RootState) => state.counter.isLoadingUserDetails
  );
  // Get user details from Redux store
  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );

  // Get plan-related state from Redux store
  const activePlan = useSelector((state: RootState) => state.user.activePlan);
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  const subscription: any = useSelector(
    (state: RootState) => state.user.subscription
  );
  const premiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const isDIYUser = useSelector((state: RootState) => state.user.isDIYUser);
  const isAgency = useSelector((state: RootState) => state.user.isAgency);
  const isDesigner = useSelector((state: RootState) => state.user.isDesigner);
  const isLocalSeoPlan = useSelector(
    (state: RootState) => state.user.isLocalSeoPlan
  );
  const isEnterprisePlan = useSelector(
    (state: RootState) => state.user.isEnterprisePlan
  );
  const isNewPlan = useSelector((state: RootState) => state.user.isNewPlan);
  const primePlan = useSelector((state: RootState) => state.user.primePlan);
  const stripeData = useSelector((state: RootState) => state.user.stripeData);
  const isDowngradedWithSites = useSelector(
    (state: RootState) => state.user.isDowngradedWithSites
  );

  // User type checks
  const roleType = userDetails?.role_type || null;
  const isMember = roleType === userRoles.member;
  const isAdmin = roleType === userRoles.admin;

  // Plan type checks
  const isFreePlan = activePlan === planDetails?.free;
  const isDiyPlan = 
    activePlan === planDetails?.diy ||
    activePlan === planNames.starter ||
    activePlan === planNames.starterPlus;
  const isDesignerPlan = activePlan === planDetails?.designer || activePlan === planDetails?.proPlus;
  const isAgencyPlan = activePlan === planDetails?.agency;
  const isLocalSeoPlanActive = activePlan === planDetails?.localseo;
  const isEnterprisePlanActive = activePlan === planDetails?.enterprise;

  // Combined plan checks
  const isDiyOrFreePlan =
    isDiyPlan || isFreePlan;
  const isPrimePlan = primePlan?.includes(activePlan) || false;
  const isProPlan = activePlan === planDetails?.pro || activePlan === planDetails?.designer || activePlan === planDetails?.proPlus;

  // Subscription status checks
  const hasSubscription =
    subscription?.subscription_id !== undefined && subscription?.subscription_id !== null;
  const isSubscriptionPaused =
    subscription?.pause_collection?.behavior !== undefined &&
    subscription?.pause_collection?.behavior !== null;

  // Plan details object with all plan information
  const planInfo = {
    activePlan,
    planDetails,
    subscription,
    premiumUser,
    isDIYUser,
    isAgency,
    isDesigner,
    isLocalSeoPlan,
    isEnterprisePlan,
    isNewPlan,
    primePlan,
    stripeData,
    isDowngradedWithSites,
    // Plan type flags
    isFreePlan,
    isDiyPlan,
    isDesignerPlan,
    isAgencyPlan,
    isLocalSeoPlanActive,
    isEnterprisePlanActive,
    isDiyOrFreePlan,
    isPrimePlan,
    isProPlan,
    // Subscription flags
    hasSubscription,
    isSubscriptionPaused
  };

  // User type object
  const userType = {
    roleType,
    isMember,
    isAdmin,
    userDetails
  };

  return {
    // User type information
    userType,
    roleType,
    isMember,
    isAdmin,
    userDetails,
    isLoadingUserDetails,

    // Plan details
    planDetails: planInfo,
    activePlan,
    planDetailsMap: planDetails,
    subscription,
    premiumUser,
    stripeData,

    // Plan type flags
    isFreePlan,
    isDiyPlan,
    isDesignerPlan,
    isAgencyPlan,
    isLocalSeoPlanActive,
    isEnterprisePlanActive,
    isDiyOrFreePlan,
    isPrimePlan,
    isProPlan,

    // User plan flags (from Redux)
    isDIYUser,
    isAgency,
    isDesigner,
    isLocalSeoPlan,
    isEnterprisePlan,
    isNewPlan,
    isDowngradedWithSites,

    // Subscription flags
    hasSubscription,
    isSubscriptionPaused,

    // Prime plan array
    primePlan
  };
};

